import * as graphics from "@client/app/graphics";
import {ContentModal} from "@client/components/Modal";
import {Scene} from "@client/components/Scene";
import {Shell} from "@client/components/Shell";
import * as constants from "@client/constants";
import * as dialogs from "@client/features/dialogs";
import {leangains} from "@client/features/planner";
import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as onboarding from "@client/journeys/onboarding";
import {RegistrationJourney} from "@client/journeys/registration";
import * as analytics from "@client/shared/analytics";
import {Static} from "@client/shared/components/Static";
import * as typography from "@client/shared/typography";
import {
    ImperialCaloriesContext,
    useSiteContext,
    useStaticURL,
} from "@client/site/context";
import {appStyles} from "@client/style";
import * as utils from "@client/utils";
import {server} from "@reactivated";
import Decimal from "decimal.js";
import {produce} from "immer";
import React from "react";

type Route =
    | "profile"
    | "goal"
    | "activity"
    | "nutrition"
    | "settings"
    | "maxes"
    | "results"
    | "program"
    | "review";

type Action =
    | {route: Route; onClick?: never; title?: React.ReactNode; disabled?: boolean}
    | {
          route?: never;
          onClick: () => unknown;
          title?: React.ReactNode;
          disabled?: boolean;
      };

type Nutrition = "STANDARD" | "PROTEIN" | "BALANCED" | "CUSTOM";

function getStep(
    router: {route: Route; push: (route: Route) => void},
    nutritionForm: ReturnType<typeof useNutritionForm>,
    workouts: server.journal.templates.Leangains["workouts"],
    handler: ReturnType<typeof onboarding.useHooks>,
): {content: React.ReactNode; back: Action | null; next: Action} {
    const smallestPlate = new Decimal(
        handler.liveProfile.is_imperial ? "2.5" : "1",
    ).times(2);

    const getHumanWeight = (input: Decimal) => {
        return utils.roundNumberToNearest(input, smallestPlate);
    };

    const exerciseTypeSchema = workouts.exercise_types
        .map((exerciseType) => {
            // https://leangains.com/reverse-pyramid-training-guide/
            // Deadlift, squat, weighted chin-up and row: 10%
            // Bench press, overhead press and seal row: 5%
            // Assistance/accessory movements: 10%
            if (exerciseType.uuid == constants.EXERCISE_TYPES.DEADLIFT) {
                return {
                    exerciseType,
                    topSetRepsTarget: 6,
                    numberOfSets: 2,
                    backoffPercentage: 10,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.SQUAT) {
                return {
                    exerciseType,
                    topSetRepsTarget: 10,
                    numberOfSets: 3,
                    backoffPercentage: 10,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.BENCH) {
                return {
                    exerciseType,
                    topSetRepsTarget: 8,
                    numberOfSets: 3,
                    backoffPercentage: 5,
                };
            }

            if (exerciseType.uuid == constants.EXERCISE_TYPES.PRESS) {
                return {
                    exerciseType,
                    topSetRepsTarget: 8,
                    numberOfSets: 3,
                    backoffPercentage: 5,
                };
            }
            return null;
        })
        .filter((t) => t != null);

    const calculations = exerciseTypeSchema
        .map(({exerciseType, topSetRepsTarget, numberOfSets, backoffPercentage}) => {
            const reportedWeight = handler.maxes[exerciseType.uuid]?.weight;
            const reportedReps = handler.maxes[exerciseType.uuid]?.reps;

            if (reportedWeight == null || reportedReps == null) {
                return null;
            }

            const weight = new Decimal(reportedWeight);
            const oneRepMax = weight.times(
                new Decimal(1).plus(new Decimal(reportedReps).dividedBy(30)),
            );
            const estimatedWeight = oneRepMax.dividedBy(
                new Decimal(1).plus(new Decimal(topSetRepsTarget).dividedBy(30)),
            );

            return {
                exerciseType,
                oneRepMax,
                sets: [...Array(numberOfSets).keys()].map((index) => ({
                    weight: estimatedWeight
                        .minus(
                            estimatedWeight
                                .times(backoffPercentage * index)
                                .dividedBy(100),
                        )
                        .dividedBy(handler.liveProfile.is_imperial ? "0.45359237" : 1),
                    reps: topSetRepsTarget + index * 2,
                })),
            };
        })
        .filter((c) => c != null);

    const schedule = [
        {
            title: "Monday",
            exerciseTypes: calculations.filter(
                (c) =>
                    c.exerciseType.uuid == constants.EXERCISE_TYPES.DEADLIFT ||
                    c.exerciseType.uuid == constants.EXERCISE_TYPES.PRESS,
            ),
            accessories: ["Calves, biceps or triceps – 2 x 10"],
        },
        {
            title: "Wednesday",
            exerciseTypes: calculations.filter(
                (c) => c.exerciseType.uuid == constants.EXERCISE_TYPES.BENCH,
            ),
            accessories: ["Rows – 3 x 8", "Calves, biceps or triceps – 2 x 10"],
        },
        {
            title: "Friday",
            exerciseTypes: calculations.filter(
                (c) => c.exerciseType.uuid == constants.EXERCISE_TYPES.SQUAT,
            ),
            accessories: [
                "Weighted Chin-Up – 3 x 8",
                "Calves, biceps or triceps – 2 x 10",
            ],
        },
    ];

    function generateScheduleContent(
        schedule: {
            title: string;
            exerciseTypes: {
                exerciseType: {name: string};
                oneRepMax: Decimal;
                sets: {
                    weight: Decimal;
                    reps: number;
                }[];
            }[];
            accessories: string[];
        }[],
    ): {headers: string[]; rows: string[][]} {
        // Headers are just the titles of the schedule
        const headers = schedule.map((day) => day.title);

        // Create arrays for each day's content (columns)
        const columns = schedule.map((day) => {
            const col: string[] = [];

            // Add exercise names and their sets
            day.exerciseTypes.forEach((exercise) => {
                col.push(exercise.exerciseType.name); // Exercise name
                exercise.sets.forEach((set) => {
                    col.push(`${getHumanWeight(set.weight).toString()}x${set.reps}`); // Set details
                });
            });

            // Add accessories after the exercises
            day.accessories.forEach((accessory) => {
                col.push(accessory); // Accessory details
            });

            return col;
        });

        // Determine the maximum number of rows
        const maxRows = Math.max(...columns.map((col) => col.length));

        // Pad shorter columns with empty cells and convert columns to rows
        const rows: string[][] = Array.from({length: maxRows}, (_, rowIndex) => {
            return schedule.map((_, dayIndex) => columns[dayIndex][rowIndex] || "");
        });

        return {headers, rows};
    }
    function formatTable(headers: string[], rows: string[][]): string {
        // Create the header row
        const headerRow = "|" + headers.join("|") + "|";

        // Create the separator row
        const separatorRow = "|" + headers.map(() => ":-").join("|") + "|";

        // Build the content rows
        const tableRows = rows.map((row) => "|" + row.join("|") + "|");

        // Combine all parts into the final string
        return [headerRow, separatorRow, ...tableRows].join("\n");
    }

    const {headers, rows} = generateScheduleContent(schedule);

    const formatEnum = (value: string) => {
        const replaced = value.replace("_", " ").toLowerCase();
        return replaced[0].toUpperCase() + replaced.slice(1);
    };
    // Then, format the table with separators and pipes
    const markdown = [];

    markdown.push("## Stats");
    markdown.push(
        formatTable(
            ["BMR", "TDEE", "Age", "Weight", "Height"],
            [
                [
                    handler.leangains.results.bmr,
                    handler.leangains.results.tdee,
                    handler.age?.toString() ?? "-",
                    utils.formatDecimalAndUnit(
                        handler.liveProfile.weight != null
                            ? new Decimal(handler.liveProfile.weight)
                                  .dividedBy(
                                      handler.liveProfile.is_imperial
                                          ? "0.45359237"
                                          : 1,
                                  )
                                  .toDecimalPlaces(1)
                            : null,
                        "weight",
                        handler.liveProfile.is_imperial,
                    ),
                    utils.formatDecimalAndUnit(
                        handler.liveProfile.height != null
                            ? new Decimal(handler.liveProfile.height)
                                  .dividedBy(
                                      handler.liveProfile.is_imperial ? "2.54" : 1,
                                  )
                                  .toDecimalPlaces(1)
                            : null,
                        "length",
                        handler.liveProfile.is_imperial,
                    ),
                ],
            ],
        ),
    );
    markdown.push("## Settings");
    markdown.push(
        formatTable(
            ["Goal", "Activity", "Nutrition"],
            [
                [
                    handler.settingsForm.values.goal,
                    handler.settingsForm.values.activity,
                    nutritionForm.value,
                ].map(formatEnum),
            ],
        ),
    );
    markdown.push("## Macros");
    markdown.push(
        formatTable(
            ["Day", "Calories", "Protein", "Fat", "Carbs"],
            handler.leangains.results.targets.map((target) => [
                target.name,
                target.calories_target,
                target.protein_target,
                target.fat_target,
                target.carbs_target,
            ]),
        ),
    );
    markdown.push("## Maxes");
    markdown.push(
        formatTable(
            ["Exercise", "Weight", "Reps"],
            exerciseTypeSchema.map(({exerciseType}) => {
                const max = handler.maxes[exerciseType.uuid];
                if (max && max.weight != null && max.reps != null) {
                    const formatted = new Decimal(max.weight)
                        .dividedBy(handler.liveProfile.is_imperial ? "0.45359237" : 1)
                        .toString();
                    return [
                        exerciseType.name,
                        `${formatted} ${handler.liveProfile.is_imperial ? "lb" : "kg"}`,
                        max.reps.toString(),
                    ];
                }
                return [exerciseType.name, "-", "-"];
            }),
        ),
    );

    markdown.push("## Workouts");
    markdown.push(formatTable(headers, rows));

    if (router.route == "review") {
        const snippet = markdown.join("\n");
        const content = (
            <div className="grid grid-cols-1 gap-4 *:rounded-md *:border *:p-4 sm:grid-cols-2">
                <div>
                    {
                        getStep(
                            {route: "profile", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "goal", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "activity", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "nutrition", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "settings", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "maxes", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "results", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div>
                    {
                        getStep(
                            {route: "program", push: () => {}},
                            nutritionForm,
                            workouts,
                            handler,
                        ).content
                    }
                </div>
                <div className="space-y-2 sm:col-span-2" id="reddit">
                    <div className="mb-4 space-y-2 leading-4">
                        <h2 className="text-xl">Reddit Post</h2>
                        <p>
                            Copy the formatted text below and paste it into a Reddit
                            post to get feedback.
                        </p>
                        <p>
                            Be sure to select <strong>Markdown</strong> when editing the
                            post.
                        </p>
                    </div>
                    <textarea
                        className="min-h-[550px] w-full border-none p-0 text-[13px]/4 text-gray-700"
                        onChange={() => {}}
                        value={snippet}
                    />
                </div>
            </div>
        );

        return {
            content,
            back: {
                title: <>Guided Setup</>,
                onClick: () => {
                    router.push("profile");
                    void analytics.track("RESTARTED_CALCULATOR_JOURNEY", {
                        diet: "LEANGAINS",
                    });
                },
            },
            next: {
                title: <>Export</>,
                onClick: () => {
                    void navigator.clipboard.writeText(snippet);

                    setTimeout(() => {
                        handler.notify("Reddit post copied to clipboard", {
                            type: "info",
                        });
                    }, 1000);
                    void analytics.track("EXPORTED_CALCULATOR_RESULTS", {
                        diet: "LEANGAINS",
                    });

                    document
                        .getElementById("reddit")!
                        .scrollIntoView({behavior: "smooth"});
                },
            },
        };
    }

    switch (router.route) {
        case "profile":
            return {
                content: <onboarding.ProfileForm {...handler} />,
                back: {
                    title: <>Skip Guided Setup</>,
                    onClick: () => {
                        void analytics.track("SKIPPED_CALCULATOR_JOURNEY", {
                            diet: "LEANGAINS",
                        });
                        router.push("review");
                    },
                },
                next: {
                    disabled: handler.validatedProfile == null,
                    route: "goal",
                },
            };
        case "goal":
            return {
                content: (
                    <div className="space-y-4 *:block *:space-y-3">
                        <forms.Field
                            field={handler.settingsForm.fields.goal}
                            component={onboarding.RadioSelect}
                            group
                            label={<h2 className="text-xl">What is your goal?</h2>}
                            help={<div>Helps us calculate your caloric needs.</div>}
                            props={{
                                labels: {
                                    WEIGHT_LOSS: "Cut",
                                    HEALTH: "Recomp",
                                    BULK: "Lean bulk",
                                },
                                descriptions: {
                                    WEIGHT_LOSS: (
                                        <>
                                            Lose fat, keep muscle and lift heavy.{" "}
                                            <br className="sm:hidden" />
                                            The original Leangains protocol.
                                        </>
                                    ),
                                    AGGRESSIVE_WEIGHT_LOSS: false,
                                    HEALTH: "Slowly lose fat and build muscle while lifting heavy. Experts only.",
                                    BULK: "Put on muscle with minimal fat using macro and caloric cycling.",
                                    MASS: false,
                                },
                            }}
                        />
                    </div>
                ),
                back: {
                    route: "profile",
                },
                next: {
                    route: "activity",
                },
            };
        case "activity":
            return {
                content: <onboarding.ActivityForm {...handler} />,
                back: {
                    route: "goal",
                },
                next: {
                    route: "nutrition",
                },
            };
        case "nutrition":
            return {
                content: (
                    <div className="space-y-4 *:block *:space-y-3">
                        <forms.Field
                            field={nutritionForm}
                            component={onboarding.RadioSelect}
                            group
                            label={<h2 className="text-xl">Nutrition and macros</h2>}
                            help={<div>Pick a breakdown of macros.</div>}
                            props={{
                                descriptions: {
                                    STANDARD:
                                        "High protein. Higher fats on rest days and higher carbs on workout days.",
                                    PROTEIN:
                                        "Extra high on protein. Helps with satiety. Great for cutting.",
                                    BALANCED:
                                        "High protein, but no fat/carbs cycling across workout and rest days.",
                                    CUSTOM: "Tweak all these settings yourself to come up with custom macros.",
                                },
                            }}
                        />
                    </div>
                ),
                back: {
                    route: "activity",
                },
                next: {
                    route: nutritionForm.value == "CUSTOM" ? "settings" : "results",
                },
            };
        case "settings": {
            const getPercentages = (value: string | null) => {
                if (value == null) {
                    return {
                        fat: 0,
                        carbs: 0,
                    };
                }
                const fat = Math.round(parseFloat(value) * 100);
                return {
                    fat,
                    carbs: 100 - fat,
                };
            };

            const workoutNutrients = getPercentages(
                handler.leangains.form.values.workout_day_fat,
            );
            const restDayNutrients = getPercentages(
                handler.leangains.form.values.rest_day_fat,
            );

            return {
                content: (
                    <div className="space-y-6">
                        <div className="flex flex-col gap-2 *:space-y-2">
                            <h2 className="text-xl">Nutrition settings</h2>
                            <label>
                                <div>Grams of protein</div>
                                <forms.Field
                                    field={
                                        handler.leangains.form.fields.protein_multiplier
                                    }
                                    noLabel
                                />
                            </label>
                        </div>
                        <div className="flex flex-col gap-4 rounded-md border px-2 py-4 *:space-y-1">
                            <h3 className="text-lg/3">Workout days</h3>
                            <label>
                                <div>Surplus or deficit</div>
                                <forms.Field
                                    field={
                                        handler.leangains.form.fields
                                            .workout_day_variance
                                    }
                                    noLabel
                                    suffix="%"
                                />
                            </label>
                            <label>
                                <div>Nutrient split</div>
                                <div className="flex items-center gap-1">
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Fat {workoutNutrients.fat} %
                                    </div>
                                    <div className="flex-1">
                                        <forms.Field
                                            field={
                                                handler.leangains.form.fields
                                                    .workout_day_fat
                                            }
                                            noLabel
                                        />
                                    </div>
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Carbs {workoutNutrients.carbs} %
                                    </div>
                                </div>
                            </label>
                        </div>
                        <div className="flex flex-col gap-4 rounded-md border px-2 py-4 *:space-y-1">
                            <h3 className="text-lg/3">Rest days</h3>
                            <label>
                                <div>Surplus or deficit</div>
                                <forms.Field
                                    field={
                                        handler.leangains.form.fields.rest_day_variance
                                    }
                                    noLabel
                                    suffix="%"
                                />
                            </label>
                            <label>
                                <div>Nutrient split</div>
                                <div className="flex items-center gap-1">
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Fat {restDayNutrients.fat} %
                                    </div>
                                    <div className="flex-1">
                                        <forms.Field
                                            field={
                                                handler.leangains.form.fields
                                                    .rest_day_fat
                                            }
                                            noLabel
                                        />
                                    </div>
                                    <div className="min-w-24 rounded-md bg-sky-100 px-1 py-0.5 text-center text-xs sm:px-2">
                                        Carbs {restDayNutrients.carbs} %
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                ),
                back: {
                    route: "nutrition",
                },
                next: {
                    onClick: () => {
                        if (forms.ready(handler.leangains.form)) {
                            router.push("results");
                        }
                    },
                },
            };
        }
        case "results":
            return {
                content: (
                    <div className="flex h-full min-h-[520px] flex-col justify-center gap-8 text-center *:block *:space-y-3">
                        <div className="text-3xl text-pretty">
                            Eat{" "}
                            <span className="text-indigo-700">
                                {handler.leangains.results.targets[0].calories_target}
                            </span>{" "}
                            calories on workout days
                        </div>
                        <div className="text-3xl text-pretty">
                            And{" "}
                            <span className="text-indigo-700">
                                {handler.leangains.results.targets[1].calories_target}
                            </span>{" "}
                            calories on rest days
                        </div>
                        <div className="text-xl/8 text-pretty">
                            <div>
                                Get{" "}
                                <span className="text-indigo-700">
                                    {Math.round(
                                        handler.leangains.results.targets[0].protein,
                                    )}
                                </span>{" "}
                                grams of{" "}
                                <span className="text-indigo-700">protein</span> every
                                day
                            </div>
                            <div>
                                At least{" "}
                                <span className="text-indigo-700">
                                    {Math.round(
                                        handler.leangains.results.targets[0].carbs,
                                    )}
                                </span>{" "}
                                grams of <span className="text-indigo-700">carbs</span>{" "}
                                on workout days
                            </div>
                            <div>
                                And at least{" "}
                                <span className="text-indigo-700">
                                    {Math.round(
                                        handler.leangains.results.targets[1].fat,
                                    )}
                                </span>{" "}
                                grams of <span className="text-indigo-700">fat</span> on
                                rest days
                            </div>
                            {/*
                            <div className='px-4'>
                                Your BMR is{" "}
                                <span className="text-indigo-700">
                                    {handler.leangains.results.bmr}
                                </span>{" "} cal
                                and your TDEE is{" "}
                                <span className="text-indigo-700">
                                    {handler.leangains.results.tdee}
                                </span> cal
                            </div>
                            */}
                        </div>
                    </div>
                ),
                back: {
                    route: nutritionForm.value == "CUSTOM" ? "settings" : "nutrition",
                },
                next: {
                    title: "Workouts",
                    route: "maxes",
                },
            };
        case "maxes":
            return {
                content: (
                    <div className="@container space-y-2">
                        <div className="px-4 py-2">
                            <h2 className="text-xl">Exercise Maxes</h2>
                            <p>
                                Pick a weight and enter how many reps you can do. Max
                                effort but <strong>not to failure</strong>.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
                            {exerciseTypeSchema.map(
                                ({exerciseType, topSetRepsTarget}) => (
                                    <div
                                        key={exerciseType.uuid}
                                        className="rounded-lg bg-indigo-100 px-4 py-3"
                                    >
                                        <h2 className="text-base">
                                            {exerciseType.name}
                                        </h2>
                                        <div className="mt-1 flex items-center gap-2">
                                            <widgets.WeightInput
                                                isImperial={
                                                    handler.liveProfile.is_imperial
                                                }
                                                name={`${exerciseType.uuid}-weight`}
                                                value={
                                                    handler.maxes[exerciseType.uuid]
                                                        ?.weight ?? null
                                                }
                                                onChange={(value) => {
                                                    handler.setMaxes(
                                                        produce(
                                                            handler.maxes,
                                                            (draft) => {
                                                                const {uuid} =
                                                                    exerciseType;
                                                                if (
                                                                    draft[uuid] == null
                                                                ) {
                                                                    draft[uuid] = {
                                                                        weight: null,
                                                                        reps: null,
                                                                    };
                                                                }
                                                                draft[uuid].weight =
                                                                    value;
                                                            },
                                                        ),
                                                    );
                                                }}
                                            />
                                            <typography.Icon icon="custom-bolder-cross" />
                                            <widgets.Suffixed suffix="reps">
                                                <widgets.IntegerInput
                                                    name={`${exerciseType.uuid}-reps`}
                                                    placeholder={topSetRepsTarget.toString()}
                                                    value={
                                                        handler.maxes[exerciseType.uuid]
                                                            ?.reps ?? null
                                                    }
                                                    onChange={(value) => {
                                                        handler.setMaxes(
                                                            produce(
                                                                handler.maxes,
                                                                (draft) => {
                                                                    const {uuid} =
                                                                        exerciseType;
                                                                    if (
                                                                        draft[uuid] ==
                                                                        null
                                                                    ) {
                                                                        draft[uuid] = {
                                                                            weight: null,
                                                                            reps: null,
                                                                        };
                                                                    }
                                                                    draft[uuid].reps =
                                                                        value;
                                                                },
                                                            ),
                                                        );
                                                    }}
                                                />
                                            </widgets.Suffixed>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>
                ),
                back: {
                    route: "results",
                },
                next: {
                    disabled: calculations.length != 4,
                    route: "program",
                },
            };
        case "program":
            return {
                content: (
                    <div className="space-y-2">
                        <div className="mb-4 space-y-2 px-4">
                            <h2 className="text-xl">Programming</h2>
                            <p>
                                If you hit the prescribed reps for each{" "}
                                <strong>top set</strong>, increment the weight the next
                                week.
                            </p>
                        </div>
                        {schedule.map((day) => (
                            <div
                                key={day.title}
                                className="space-y-1 rounded-lg bg-indigo-100 px-4 py-2"
                            >
                                <h2 className="text-xl">{day.title}</h2>
                                {day.exerciseTypes.map((exerciseType) => (
                                    <div
                                        key={exerciseType.exerciseType.uuid}
                                        className="ml-4"
                                    >
                                        <h2 className="text-base">
                                            {exerciseType.exerciseType.name}
                                        </h2>
                                        <ul className="list-inside">
                                            {exerciseType.sets.map((set, index) => (
                                                <li
                                                    key={index}
                                                    className="ml-1 list-disc"
                                                >
                                                    {getHumanWeight(
                                                        set.weight,
                                                    ).toString()}{" "}
                                                    {handler.liveProfile.is_imperial
                                                        ? "lb"
                                                        : "kg"}{" "}
                                                    x {set.reps}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                                {day.accessories.map((accessory) => (
                                    <div key={accessory} className="ml-4">
                                        <h2 className="text-base">{accessory}</h2>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ),
                back: {
                    route: "maxes",
                },
                next: {
                    title: "Review",
                    onClick: () => {
                        setTimeout(() => {
                            document
                                .getElementById("reddit")!
                                .scrollIntoView({behavior: "smooth"});
                        }, 500);
                        router.push("review");
                    },
                },
            };
        default: {
            const _exhaustiveCheck: never = router.route;
            return _exhaustiveCheck;
        }
    }
}

function useNutritionForm() {
    const [nutrition, setNutrition] = React.useState<Nutrition>("STANDARD");

    return forms.useField(
        {
            kind: "select",
            nullable: false,
            options: [
                ["STANDARD", "Standard"],
                ["PROTEIN", "High Protein"],
                ["BALANCED", "Balanced"],
                ["CUSTOM", "Custom"],
            ],
            required: true,
        },
        {value: nutrition, onChange: setNutrition},
    );
}

export function Leangains({workouts}: server.journal.templates.Leangains) {
    const {profile} = useSiteContext();
    const props = onboarding.useHooks({diet: "LEANGAINS", requireName: false, profile});

    React.useEffect(() => {
        if (props.settingsForm.values.goal == "WEIGHT_LOSS") {
            props.leangains.onPresetChange(0);
        } else if (props.settingsForm.values.goal == "BULK") {
            props.leangains.onPresetChange(1);
        } else {
            props.leangains.onPresetChange(2);
        }
    }, [props.settingsForm.values.goal]);

    const [route, setRoute] = React.useState<Route>("profile");

    const registrationDialog = dialogs.useDialog({
        Content: RegistrationJourney,
        modal: ContentModal,
    });

    const nutritionForm = useNutritionForm();

    const leangainsDefaults = leangains.presets[0];

    React.useEffect(() => {
        if (nutritionForm.value == "STANDARD") {
            props.leangains.form.update((prev) => ({
                ...prev,
                protein_multiplier: leangainsDefaults.protein_multiplier,
                workout_day_fat: leangainsDefaults.workout_day_fat,
                rest_day_fat: leangainsDefaults.rest_day_fat,
            }));
        } else if (nutritionForm.value == "PROTEIN") {
            props.leangains.form.update((prev) => ({
                ...prev,
                protein_multiplier: "3.3",
                workout_day_fat: leangainsDefaults.workout_day_fat,
                rest_day_fat: leangainsDefaults.rest_day_fat,
            }));
        } else if (nutritionForm.value == "BALANCED") {
            props.leangains.form.update((prev) => ({
                ...prev,
                protein_multiplier: leangainsDefaults.protein_multiplier,
                workout_day_fat: "0.5",
                rest_day_fat: "0.5",
            }));
        }
    }, [nutritionForm.value]);

    const {content, back, next} = getStep(
        {route, push: setRoute},
        nutritionForm,
        workouts,
        props,
    );

    const registrationPrompt = (
        <div className="border-b px-3">
            <div className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-4">
                    <graphics.Logo className="size-16" />
                    <div className="text-pretty">
                        Take control of your health.{" "}
                        <strong className="text-indigo-700">Quickly.</strong>
                    </div>
                </div>
                <button
                    onClick={() => registrationDialog}
                    type="button"
                    className="rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
                >
                    Sign Up
                </button>
            </div>
        </div>
    );

    const bottomSection = (
        <div className="flex flex-row-reverse justify-between bg-gray-50 px-4 py-3">
            <button
                type="button"
                disabled={next.disabled}
                onClick={() => {
                    if (next.route != null) {
                        void analytics.track("CLICKED_CALCULATOR_JOURNEY_NEXT", {
                            route_name: next.route,
                            diet: "LEANGAINS",
                        });
                        setRoute(next.route);
                    } else {
                        next.onClick();
                    }
                }}
                className="ml-auto rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid disabled:opacity-50"
            >
                {next.title == undefined || typeof next.title == "string" ? (
                    <typography.Icon
                        icon="custom-chevron-right"
                        large="medium"
                        align="right"
                    >
                        {next.title ?? "Next"}
                    </typography.Icon>
                ) : (
                    next.title
                )}
            </button>
            {back != null && (
                <button
                    type="button"
                    className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                    onClick={() => {
                        if (back.route != null) {
                            void analytics.track("CLICKED_CALCULATOR_JOURNEY_BACK", {
                                route_name: back.route,
                                diet: "LEANGAINS",
                            });
                            setRoute(back.route);
                        } else {
                            back.onClick();
                        }
                    }}
                >
                    {back.title == undefined || typeof back.title == "string" ? (
                        <typography.Icon
                            icon="custom-chevron-left"
                            large="medium"
                            align="left"
                        >
                            {back.title ?? "Back"}
                        </typography.Icon>
                    ) : (
                        back.title
                    )}
                </button>
            )}
        </div>
    );

    return (
        <ImperialCaloriesContext.Provider
            value={{isImperial: props.liveProfile.is_imperial, setIsImperial: () => {}}}
        >
            <div className={appStyles.app}>
                {registrationDialog.dialog}
                <div className="hidden">{registrationPrompt}</div>
                <div
                    className={appStyles.navigationAndManager}
                    style={{borderBottom: "1px solid #CCC", marginBottom: 10}}
                >
                    <div className="mx-auto flex w-full max-w-[1000px] items-center gap-1 px-4 py-3">
                        <typography.Icon
                            icon="joy"
                            large="xl"
                            className="text-gray-500"
                        />
                        <div className="flex-1 text-center text-base font-medium">
                            Leangains Guide
                        </div>
                        <div className="size-[26px]" />
                    </div>
                </div>

                <Scene
                    actions={[]}
                    tabs={[]}
                    topSection={null}
                    bottomSection={bottomSection}
                    more={null}
                    locator={null}
                >
                    {content}
                </Scene>
            </div>
        </ImperialCaloriesContext.Provider>
    );
}

export function Template(props: server.journal.templates.Leangains) {
    const STATIC_URL = useStaticURL();
    analytics.useTrackPageView();

    return (
        <Shell>
            <Static />
            <meta charSet="UTF-8" />
            <title>Leangains Guide | Joy Health Tracker</title>
            <meta
                name="viewport"
                content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
            />
            <link rel="icon" href={`${STATIC_URL}favicon.png`} type="image/png" />
            <Leangains {...props} />
        </Shell>
    );
}
